import React from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import './SharedHosting.css';

const SharedHosting = () => {
    const scrollToPricing = (e) => {
        e.preventDefault();
        const pricingSection = document.getElementById('pricing');
        if (pricingSection) {
            const headerOffset = 100;
            const elementPosition = pricingSection.getBoundingClientRect().top;
            const offsetPosition = elementPosition + window.pageYOffset - headerOffset;
            
            window.scrollTo({
                top: offsetPosition,
                behavior: 'smooth'
            });
        }
    };

    return (
        <>
            <Helmet>
                {/* Primary Meta Tags */}
                <title>Shared Web Hosting - Fast, Secure & Affordable Plans | Epicfinal</title>
                <meta name="title" content="Shared Web Hosting - Fast, Secure & Affordable Plans | Epicfinal" />
                <meta name="description" content="Professional shared hosting from $5.99/mo. NVMe SSD storage, free SSL, Cloudflare CDN, 1-click WordPress install, WAF protection & 24/7 support. Perfect for websites, blogs & small businesses." />
                <meta name="keywords" content="shared hosting, web hosting, cheap hosting, affordable hosting, website hosting, blog hosting, small business hosting, NVMe hosting, SSD hosting, Cloudflare hosting, SSL hosting, WordPress hosting, cPanel hosting, cloud hosting" />
                <meta name="robots" content="index, follow, max-snippet:-1, max-image-preview:large, max-video-preview:-1" />
                <meta name="author" content="Epicfinal" />
                <link rel="canonical" href="https://epicfinal.com/shared-hosting" />

                {/* Language & Locale */}
                <html lang="en" />
                <meta property="og:locale" content="en_US" />
                <link rel="alternate" href="https://epicfinal.com/shared-hosting" hrefLang="en" />
                <link rel="alternate" href="https://epicfinal.com/shared-hosting" hrefLang="x-default" />

                {/* Open Graph / Facebook */}
                <meta property="og:type" content="website" />
                <meta property="og:url" content="https://epicfinal.com/shared-hosting" />
                <meta property="og:title" content="Shared Web Hosting - Fast, Secure & Affordable | Epicfinal" />
                <meta property="og:description" content="Professional shared hosting from $5.99/mo. NVMe SSD storage, free SSL, Cloudflare CDN & 24/7 support. Launch your website today!" />
                <meta property="og:image" content="https://epicfinal.com/android-chrome-512x512.png" />
                <meta property="og:image:alt" content="Epicfinal Shared Hosting - Fast & Affordable Web Hosting" />
                <meta property="og:site_name" content="Epicfinal" />

                {/* Twitter */}
                <meta name="twitter:card" content="summary_large_image" />
                <meta name="twitter:url" content="https://epicfinal.com/shared-hosting" />
                <meta name="twitter:title" content="Shared Web Hosting - Fast, Secure & Affordable | Epicfinal" />
                <meta name="twitter:description" content="Professional shared hosting from $5.99/mo. NVMe SSD, free SSL, Cloudflare CDN & 24/7 support." />
                <meta name="twitter:image" content="https://epicfinal.com/android-chrome-512x512.png" />

                {/* Schema.org Structured Data */}
                <script type="application/ld+json">{`
                    {
                        "@context": "https://schema.org",
                        "@type": "WebPage",
                        "name": "Shared Web Hosting - Fast, Secure & Affordable Plans",
                        "description": "Professional shared hosting from $5.99/mo with NVMe SSD storage, free SSL, Cloudflare CDN integration, and 24/7 expert support.",
                        "url": "https://epicfinal.com/shared-hosting",
                        "provider": {
                            "@type": "Organization",
                            "name": "Epicfinal",
                            "url": "https://epicfinal.com"
                        },
                        "breadcrumb": {
                            "@type": "BreadcrumbList",
                            "itemListElement": [
                                {
                                    "@type": "ListItem",
                                    "position": 1,
                                    "name": "Home",
                                    "item": "https://epicfinal.com"
                                },
                                {
                                    "@type": "ListItem",
                                    "position": 2,
                                    "name": "Shared Hosting",
                                    "item": "https://epicfinal.com/shared-hosting"
                                }
                            ]
                        }
                    }
                `}</script>
                <script type="application/ld+json">{`
                    {
                        "@context": "https://schema.org",
                        "@type": "Product",
                        "name": "Epicfinal Shared Hosting",
                        "description": "Reliable shared hosting with NVMe SSD storage, free SSL, Cloudflare integration, and 24/7 support",
                        "brand": {
                            "@type": "Brand",
                            "name": "Epicfinal"
                        },
                        "offers": [
                            {
                                "@type": "Offer",
                                "name": "epicfinal Starter",
                                "price": "5.99",
                                "priceCurrency": "USD",
                                "priceValidUntil": "2025-12-31",
                                "availability": "https://schema.org/InStock",
                                "url": "https://my.epicfinal.com/index.php?rp=/store/cloud-web-hosting/s1-web-starter"
                            },
                            {
                                "@type": "Offer",
                                "name": "epicfinal Cloud – Web Pro",
                                "price": "8.99",
                                "priceCurrency": "USD",
                                "priceValidUntil": "2025-12-31",
                                "availability": "https://schema.org/InStock",
                                "url": "https://my.epicfinal.com/index.php?rp=/store/cloud-web-hosting/epicfinal-cloud-web-pro"
                            },
                            {
                                "@type": "Offer",
                                "name": "epicfinal Business",
                                "price": "15.99",
                                "priceCurrency": "USD",
                                "priceValidUntil": "2025-12-31",
                                "availability": "https://schema.org/InStock",
                                "url": "https://my.epicfinal.com/index.php?rp=/store/cloud-web-hosting/epicfinal-business"
                            }
                        ],
                        "aggregateRating": {
                            "@type": "AggregateRating",
                            "ratingValue": "4.8",
                            "reviewCount": "150",
                            "bestRating": "5",
                            "worstRating": "1"
                        }
                    }
                `}</script>
                <script type="application/ld+json">{`
                    {
                        "@context": "https://schema.org",
                        "@type": "FAQPage",
                        "mainEntity": [
                            {
                                "@type": "Question",
                                "name": "What is shared hosting?",
                                "acceptedAnswer": {
                                    "@type": "Answer",
                                    "text": "Shared hosting is an affordable web hosting solution where multiple websites share server resources. It's perfect for personal websites, blogs, and small businesses with moderate traffic."
                                }
                            },
                            {
                                "@type": "Question",
                                "name": "What's included in Epicfinal shared hosting?",
                                "acceptedAnswer": {
                                    "@type": "Answer",
                                    "text": "All shared hosting plans include NVMe SSD storage, free SSL certificates, Cloudflare CDN integration, WAF protection, malware scanning, email accounts, 1-click WordPress install, and 24/7 support."
                                }
                            },
                            {
                                "@type": "Question",
                                "name": "How much does shared hosting cost?",
                                "acceptedAnswer": {
                                    "@type": "Answer",
                                    "text": "Epicfinal shared hosting starts at $5.99/month for the Starter plan with 1 website and 5GB storage, $8.99/month for the Pro plan with up to 3 websites, and $15.99/month for the Business plan with up to 10 websites."
                                }
                            }
                        ]
                    }
                `}</script>
            </Helmet>

            <div className="shared-hosting-page">
                {/* Hero Section */}
                <section className="shared-hero">
                    <div className="container">
                        <div className="shared-hero-content">
                            <h1 className="shared-hero-title">
                                Shared Hosting
                            </h1>
                            <p className="shared-hero-subtitle">
                                Affordable, reliable hosting for your website
                            </p>
                            <p className="shared-hero-description">
                                Perfect for personal websites, blogs, and small businesses. Get your website online quickly with our easy-to-use shared hosting plans.
                            </p>
                            <div className="shared-hero-buttons">
                                <a href="#pricing" onClick={scrollToPricing} className="btn-shared btn-primary">
                                    View Plans & Pricing
                                </a>
                                <Link to="/contact1" className="btn-shared btn-secondary">
                                    Talk to Expert
                                </Link>
                            </div>
                        </div>
                    </div>
                </section>

                {/* Features Section */}
                <section className="shared-features">
                    <div className="container">
                        <div className="shared-section-header">
                            <h2>Why Choose Shared Hosting?</h2>
                            <p>Everything you need to launch your website</p>
                        </div>
                        <div className="shared-features-grid">
                            <div className="shared-feature-card">
                                <div className="shared-feature-icon">
                                    <i className="las la-dollar-sign"></i>
                                </div>
                                <h3>Cost-Effective</h3>
                                <p>Start from as low as $5.99/month. Perfect for budget-conscious users.</p>
                            </div>
                            <div className="shared-feature-card">
                                <div className="shared-feature-icon">
                                    <i className="las la-rocket"></i>
                                </div>
                                <h3>Easy Setup</h3>
                                <p>Get your website online in minutes with our 1-click installer.</p>
                            </div>
                            <div className="shared-feature-card">
                                <div className="shared-feature-icon">
                                    <i className="las la-shield-alt"></i>
                                </div>
                                <h3>Free SSL Certificate</h3>
                                <p>Secure your website with free SSL encryption included in all plans.</p>
                            </div>
                            <div className="shared-feature-card">
                                <div className="shared-feature-icon">
                                    <i className="las la-tachometer-alt"></i>
                                </div>
                                <h3>High Performance</h3>
                                <p>SSD storage and optimized servers ensure fast loading times.</p>
                            </div>
                            <div className="shared-feature-card">
                                <div className="shared-feature-icon">
                                    <i className="las la-headset"></i>
                                </div>
                                <h3>24/7 Support</h3>
                                <p>Our expert support team is available anytime you need help.</p>
                            </div>
                            <div className="shared-feature-card">
                                <div className="shared-feature-icon">
                                    <i className="las la-sync"></i>
                                </div>
                                <h3>Daily Backups</h3>
                                <p>Automatic daily backups keep your data safe and secure.</p>
                            </div>
                        </div>
                    </div>
                </section>

                {/* Pricing Plans Section */}
                <section className="shared-pricing" id="pricing">
                    <div className="container">
                        <div className="shared-section-header">
                            <h2>Choose Your Plan</h2>
                            <p>Flexible plans for every need</p>
                        </div>
                        <div className="shared-pricing-grid">
                            {/* Starter Plan */}
                            <div className="shared-pricing-card">
                                <div className="shared-pricing-header">
                                    <h3>epicfinal Starter</h3>
                                    <p className="shared-pricing-tagline">Designed for small businesses, freelancers and personal projects</p>
                                </div>
                                <div className="shared-pricing-price">
                                    <span className="shared-price-amount">$5.99</span>
                                    <span className="shared-price-period">USD / Monthly</span>
                                </div>
                                <ul className="shared-pricing-features">
                                    <li><i className="las la-check"></i> <strong>1 Website</strong></li>
                                    <li><i className="las la-check"></i> 5GB NVMe SSD Storage</li>
                                    <li><i className="las la-check"></i> Unmetered Traffic (Fair Use)</li>
                                    <li><i className="las la-check"></i> Email & Databases</li>
                                    <li><i className="las la-check"></i> Up to 3 Databases</li>
                                    <li><i className="las la-check"></i> WAF – Web Application Firewall</li>
                                    <li><i className="las la-check"></i> Malware Protection</li>
                                    <li><i className="las la-check"></i> Free SSL Certificates</li>
                                    <li><i className="las la-check"></i> Cloudflare Integration</li>
                                    <li><i className="las la-check"></i> User-Friendly Control Panel</li>
                                    <li><i className="las la-check"></i> 1-Click WordPress Install</li>
                                </ul>
                                <a href="https://my.epicfinal.com/index.php?rp=/store/cloud-web-hosting/s1-web-starter" 
                                   className="shared-pricing-btn" target="_blank" rel="noopener noreferrer">
                                    Get Started
                                </a>
                            </div>

                            {/* Pro Plan */}
                            <div className="shared-pricing-card featured">
                                <div className="shared-pricing-badge">Most Popular</div>
                                <div className="shared-pricing-header">
                                    <h3>epicfinal Cloud – Web Pro</h3>
                                    <p className="shared-pricing-tagline">Advanced Hosting for Growing Websites</p>
                                </div>
                                <div className="shared-pricing-price">
                                    <span className="shared-price-amount">$8.99</span>
                                    <span className="shared-price-period">USD / Monthly</span>
                                </div>
                                <ul className="shared-pricing-features">
                                    <li><i className="las la-check"></i> <strong>Up to 3 Separate Websites</strong></li>
                                    <li><i className="las la-check"></i> 15GB NVMe SSD Storage</li>
                                    <li><i className="las la-check"></i> Unmetered Traffic (Fair Use)</li>
                                    <li><i className="las la-check"></i> WAF & Protection Against Malware</li>
                                    <li><i className="las la-check"></i> Anti-Spam Protection for Email</li>
                                    <li><i className="las la-check"></i> Free SSL for All Domains</li>
                                    <li><i className="las la-check"></i> Cloudflare Integration</li>
                                    <li><i className="las la-check"></i> 1-Click WordPress Install</li>
                                    <li><i className="las la-check"></i> Automatic WordPress Updates</li>
                                    <li><i className="las la-check"></i> Caching for Faster Page Loads</li>
                                    <li><i className="las la-check"></i> Access to Logs & Statistics</li>
                                </ul>
                                <a href="https://my.epicfinal.com/index.php?rp=/store/cloud-web-hosting/epicfinal-cloud-web-pro" 
                                   className="shared-pricing-btn" target="_blank" rel="noopener noreferrer">
                                    Get Started
                                </a>
                            </div>

                            {/* Business Plan */}
                            <div className="shared-pricing-card">
                                <div className="shared-pricing-header">
                                    <h3>epicfinal Business</h3>
                                    <p className="shared-pricing-tagline">Host multiple client projects under one powerful platform</p>
                                </div>
                                <div className="shared-pricing-price">
                                    <span className="shared-price-amount">$15.99</span>
                                    <span className="shared-price-period">USD / Monthly</span>
                                </div>
                                <ul className="shared-pricing-features">
                                    <li><i className="las la-check"></i> <strong>Up to 10 Websites</strong></li>
                                    <li><i className="las la-check"></i> 40GB NVMe SSD Storage</li>
                                    <li><i className="las la-check"></i> Unmetered Traffic (~150k Visits/Month)</li>
                                    <li><i className="las la-check"></i> Ideal for WooCommerce Stores</li>
                                    <li><i className="las la-check"></i> WordPress Multisite Support</li>
                                    <li><i className="las la-check"></i> Perfect for Agencies</li>
                                    <li><i className="las la-check"></i> Large Projects Support</li>
                                    <li><i className="las la-check"></i> Priority Support</li>
                                    <li><i className="las la-check"></i> All Pro Features Included</li>
                                    <li><i className="las la-check"></i> Advanced Security Features</li>
                                    <li><i className="las la-check"></i> Reliable Partner for Agencies</li>
                                </ul>
                                <a href="https://my.epicfinal.com/index.php?rp=/store/cloud-web-hosting/epicfinal-business" 
                                   className="shared-pricing-btn" target="_blank" rel="noopener noreferrer">
                                    Get Started
                                </a>
                            </div>
                        </div>
                    </div>
                </section>

                {/* Perfect For Section */}
                <section className="shared-perfect">
                    <div className="container">
                        <div className="shared-section-header">
                            <h2>Perfect For</h2>
                            <p>Who should choose shared hosting?</p>
                        </div>
                        <div className="shared-perfect-grid">
                            <div className="shared-perfect-card">
                                <div className="shared-perfect-icon">
                                    <i className="las la-blog"></i>
                                </div>
                                <h3>Personal Blogs</h3>
                                <p>Start your blog with an affordable and reliable hosting solution.</p>
                            </div>
                            <div className="shared-perfect-card">
                                <div className="shared-perfect-icon">
                                    <i className="las la-store"></i>
                                </div>
                                <h3>Small Business</h3>
                                <p>Get your business online with a professional website.</p>
                            </div>
                            <div className="shared-perfect-card">
                                <div className="shared-perfect-icon">
                                    <i className="las la-briefcase"></i>
                                </div>
                                <h3>Portfolio Sites</h3>
                                <p>Showcase your work with a fast and beautiful portfolio.</p>
                            </div>
                            <div className="shared-perfect-card">
                                <div className="shared-perfect-icon">
                                    <i className="las la-graduation-cap"></i>
                                </div>
                                <h3>Educational Sites</h3>
                                <p>Create educational content and online courses.</p>
                            </div>
                        </div>
                    </div>
                </section>

                {/* CTA Section */}
                <section className="shared-cta">
                    <div className="container">
                        <div className="shared-cta-content">
                            <h2>Ready to Get Started?</h2>
                            <p>Launch your website today with our reliable shared hosting</p>
                            <div className="shared-cta-buttons">
                                <Link to="/pricing" className="btn-shared btn-light">
                                    Choose Your Plan
                                </Link>
                                <Link to="/contact1" className="btn-shared btn-outline">
                                    Contact Sales
                                </Link>
                            </div>
                        </div>
                    </div>
                </section>
            </div>
        </>
    );
};

export default SharedHosting;

